import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost, apiPut, apiRemove } from 'api-service';
import { isArray, toast } from 'lib';
import translation from 'translation';
import { contentAdapter } from 'store/content/adapters';
import {
  mapApiPlatformToAdapted,
  apiPathToPlatform,
  getCurrentSlice,
  platformToReducerName,
  magicNumbers,
} from 'utils';
import { setCurrentQuery } from 'store/page/pageSlice';
import { errorHandler } from 'store/errorHandler';
import {
  prepareStreamParams,
  publishRateParams,
  postCountParams,
} from './dashboard.helpers';
import { streamAdaptor } from './dashboard.adapter';

const { t } = translation;

export const fetchDashboards = createAsyncThunk(
  'dashboardReducer/fetchDashboards',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet('/search/api/v1/dashboard');

      return response.data?.result?.data || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchStreamContents = createAsyncThunk(
  'dashboardReducer/fetchStreamContents',
  async ({ streamId, params = {} }, thunkAPI) => {
    const stream = thunkAPI
      .getState()
      .dashboardReducer.dashboards.data[0].streams.find(
        (stream) => stream.id === streamId
      );
    const platform = apiPathToPlatform[stream.params.api_path];

    try {
      const response = await apiGet('/search/api/v1/search', {
        time_duration: 21600,
        count: 50,
        sort: 'time',
        sort_type: 'desc',
        ...stream.params,
        ...params,
      });

      return (
        response?.data?.result?.data?.map((content) => {
          return contentAdapter(
            content,
            mapApiPlatformToAdapted(content?.platform) || platform
          );
        }) || []
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchStreamPublishRate = createAsyncThunk(
  'dashboardReducer/fetchStreamPublishRate',
  async ({ streamId }, thunkAPI) => {
    const stream = thunkAPI
      .getState()
      .dashboardReducer.dashboards.data[0].streams.find(
        (stream) => stream.id === streamId
      );
    const platform = apiPathToPlatform[stream.params.api_path];

    try {
      const response = await apiGet('/search/api/v1/search', {
        ...stream.params,
        ...publishRateParams(platform),
      });

      return response?.data?.result?.data?.map((publishRate, index) => ({
        count: publishRate.count,
        point: index,
      }));
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchStreamPostCount = createAsyncThunk(
  'dashboardReducer/fetchStreamPostCount',
  async ({ streamId }, thunkAPI) => {
    const stream = thunkAPI
      .getState()
      .dashboardReducer.dashboards.data[0].streams.find(
        (stream) => stream.id === streamId
      );
    const platform = apiPathToPlatform[stream.params.api_path];

    try {
      const response = await apiGet('/search/api/v1/search', {
        ...stream.params,
        ...postCountParams(platform),
      });

      const postCount =
        response?.data?.result?.data?.post_count ||
        response?.data?.result?.data?.original_post_count ||
        0;

      return parseInt(postCount / 24);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const saveStream = createAsyncThunk(
  'dashboardReducer/saveStream',
  async (_, thunkAPI) => {
    const { stream, filters, dashboardId, dashboards } =
      thunkAPI.getState().dashboardReducer;

    const params = prepareStreamParams(stream, filters);

    try {
      const response = stream.streamId
        ? await apiPut(
            `/search/api/v1/dashboard/${dashboardId}/stream/${stream.streamId}`,
            params
          )
        : await apiPost(
            `/search/api/v1/dashboard/${dashboardId}/stream`,
            params
          );

      toast({
        title: t('common:successOperation'),
        description: stream.streamId
          ? t('dashboard:success.editStream.description')
          : t('dashboard:success.addStream.description'),
      });

      if (!isArray(dashboards.data?.[0]?.streams)) {
        await thunkAPI.dispatch(fetchDashboards());
        return null;
      }

      return stream.streamId ? params : response.data?.result?.data;
    } catch (error) {
      errorHandler({
        message: {
          description: stream.streamId
            ? t('dashboard:error.editStream.description')
            : t('dashboard:error.addStream.description'),
        },
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteStream = createAsyncThunk(
  'dashboardReducer/deleteStream',
  async ({ dashboardId, streamId }, thunkAPI) => {
    try {
      await apiRemove(
        `/search/api/v1/dashboard/${dashboardId}/stream/${streamId}`
      );
      toast({
        title: t('common:successOperation'),
        description: t('dashboard:success.deleteStream.description'),
      });
    } catch (error) {
      errorHandler({
        message: { description: t('dashboard:error.deleteStream.description') },
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const applyFiltersToContents = createAsyncThunk(
  'dashboardReducer/applyFiltersToContents',
  async ({ platform, data }, thunkAPI) => {
    const { filters } = streamAdaptor(data);

    thunkAPI.dispatch(
      setCurrentQuery({
        queryId: data?.params.query || magicNumbers.MEGA_SUBJECT_QUERY_ID,
      })
    );

    const { applyWebsiteSettingsFilters, filtersInitialState, sortBy } =
      getCurrentSlice(platformToReducerName[platform]);

    if (typeof applyWebsiteSettingsFilters === 'function') {
      thunkAPI.dispatch(
        applyWebsiteSettingsFilters({
          filters:
            platform === 'multiPlatform'
              ? {
                  ...filters[platform],
                  platformFilter: {
                    hasFilter: true,
                    selectedIds: data?.params.platforms,
                  },
                }
              : filters[platform],
          sortBy,
          filtersInitialState,
        })
      );
    }
  }
);
